import { Injectable, Injector } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'src/environments/environment';
import { PatientService } from './patient.service';
import * as auth from 'auth0-js';
import { LocaleService } from './locale.service';
import { RegistrationService } from './registration.service';
import * as moment from 'moment';
import { Browser } from '@capacitor/browser';
import { CapacitorService } from './capacitor.service';
import { AppPaths } from '../enums/app-paths.enum';
import { RequestService } from './request.service';
import { LocalStorageService } from './local-storage.service';
import { CustomRouter } from '../routers/custom-router';
import { WebServiceStateHandler } from './web-service-state-handler.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from '../modules/shared/components/dialog/dialog.component';
import { AppointmentService } from './appointment.service';
import { LoadingSpinnerService } from './loading-spinner.service';
import { CallService } from './call.service';
import { AuthenticationBackend } from '../models/authentication-backend.model';
import { Provider } from '../models/provider.model';
import { IdentityPlatformAuthenticationService } from './identity-platform-authentication.service';
import { IdentityPlatformSocialMethod } from './identity-platform-authentication.service';
import { isNativeApp } from 'src/app/util/app-platform';

const REFRESH_TOKEN_ATTEMPTS = 3;
const REFRESH_TOKEN_SECONDS_BEFORE_EXPIRE = 30;
const AUTHENTICATION_BACKEND_KEY = 'authentication_backend';
const IDENTITY_PLATFORM_PROVIDER_ID_KEY = 'identity_platform_provider_id';

type Auth0TokenBody = {
  email: string;
  data?: {
    isNewPatient?: boolean;
    preUpdateData?: {
      birthDate: number;
    };
    geolocationProvidedByToken?: boolean;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  auth0: auth.WebAuth;
  auth0PS!: auth.WebAuth;

  static refreshingToken = false;
  static refreshTokenCbs: any[] = [];
  static refreshAttempts = REFRESH_TOKEN_ATTEMPTS;
  private handlingUnauthorized = false;

  private sessionSyncInitialized = false;

  constructor(
    public jwtHelper: JwtHelperService,
    private registrationService: RegistrationService,
    private localStorageService: LocalStorageService,
    private identityPlatformAuthenticationService: IdentityPlatformAuthenticationService,
    private injector: Injector
  ) {
    this.auth0 = new auth.WebAuth({
      domain: environment.AUTH0_DOMAIN,
      clientID: environment.AUTH0_CLIENTID,
    });
  }

  // Resolve heavyweight collaborators lazily to avoid root DI cycles during app bootstrap.
  private get customRouter(): CustomRouter {
    return this.injector.get(CustomRouter);
  }

  private get patientService(): PatientService {
    return this.injector.get(PatientService);
  }

  private get localeService(): LocaleService {
    return this.injector.get(LocaleService);
  }

  private get capacitorService(): CapacitorService {
    return this.injector.get(CapacitorService);
  }

  private get requestService(): RequestService {
    return this.injector.get(RequestService);
  }

  private get webServiceStateHandler(): WebServiceStateHandler {
    return this.injector.get(WebServiceStateHandler);
  }

  private get matDialog(): MatDialog {
    return this.injector.get(MatDialog);
  }

  private get appointmentService(): AppointmentService {
    return this.injector.get(AppointmentService);
  }

  private get spinnerService(): LoadingSpinnerService {
    return this.injector.get(LoadingSpinnerService);
  }

  private get callService(): CallService {
    return this.injector.get(CallService);
  }

  initializeSessionSync(): void {
    if (this.sessionSyncInitialized || typeof window === 'undefined') return;

    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.storageArea !== localStorage || (event.key !== 'id_token' && event.key !== null)) return;
      if (event.key === 'id_token' && this.isSameSession(event.oldValue, event.newValue)) return;

      this.callService.endCallFlow();
      window.location.replace('/');
    });
    this.sessionSyncInitialized = true;
  }

  public getAuth0(connection?: string): auth.WebAuth {
    // Workaround in dev to get PS auth0 configuration
    if(environment.ENVIRONMENT !== 'production' && connection === environment.PS_AUTH0_CONNECTION)
      return this.getAuth0PS();
    return this.auth0;
  }

  private getAuth0PS(): auth.WebAuth {
    if(this.auth0PS) return this.auth0PS;
    this.auth0PS = new auth.WebAuth({
      domain: 'https://login-dev.login-dev-gruposancorseguros.auth0app.com',
      clientID: 'Wv19vjPHIkoYabix0LjhNdakrrrwP6UM',
    });
    return this.auth0PS;
  }

  public getToken(): any {
    const token = localStorage.getItem('id_token')
    if(token === 'undefined') {
      this.localStorageService.clear();
      return undefined;
    }
    return token;
  }

  async initializeSession(): Promise<void> {
    this.identityPlatformAuthenticationService.validateConfiguration();
    if (this.hasValidLegacyToken()) {
      if (!localStorage.getItem(AUTHENTICATION_BACKEND_KEY)) {
        const provider = this.registrationService.getProvider();
        const backend = provider?.authProvider || (provider?.useToken ? 'token' : 'auth0');
        localStorage.setItem(AUTHENTICATION_BACKEND_KEY, backend);
      }
      return;
    }

    await this.identityPlatformAuthenticationService.initialize();
    if (this.usesIdentityPlatform() && !this.identityPlatformAuthenticationService.hasSession()) {
      this.clearIdentityPlatformContext();
    }
  }

  setAuthenticationProvider(provider: Provider): void {
    this.registrationService.setProvider(provider);
    const backend = provider.patientLoginConfiguration?.backend || provider.authProvider;
    if (!backend) throw new Error('Provider authentication backend is missing.');
    localStorage.setItem(AUTHENTICATION_BACKEND_KEY, backend);

    if (backend === 'identityPlatform') {
      localStorage.setItem(IDENTITY_PLATFORM_PROVIDER_ID_KEY, provider.id);
    } else {
      localStorage.removeItem(IDENTITY_PLATFORM_PROVIDER_ID_KEY);
    }
  }

  getAuthenticationBackend(): AuthenticationBackend | null {
    const backend = localStorage.getItem(AUTHENTICATION_BACKEND_KEY);
    if (backend === 'auth0' || backend === 'identityPlatform' || backend === 'token') return backend;
    return null;
  }

  usesIdentityPlatform(): boolean {
    return this.getAuthenticationBackend() === 'identityPlatform'
      && !!this.getIdentityPlatformProviderId();
  }

  getIdentityPlatformProviderId(): string | undefined {
    if (this.getAuthenticationBackend() !== 'identityPlatform') return undefined;
    return localStorage.getItem(IDENTITY_PLATFORM_PROVIDER_ID_KEY) || undefined;
  }

  getIdentityPlatformToken(forceRefresh = false): Promise<string | null> {
    if (!this.usesIdentityPlatform()) return Promise.resolve(null);
    return this.identityPlatformAuthenticationService.getIdToken(forceRefresh);
  }

  hasSessionContext(): boolean {
    return this.hasValidLegacyToken()
      || (this.usesIdentityPlatform() && this.identityPlatformAuthenticationService.hasSession());
  }

  public getRefreshToken(): any {
    return localStorage.getItem("refresh_token");
  }

  public setItem(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  public isAuthenticated(): boolean {
    return this.hasSessionContext();
  }

  public validateAuthentication(idToken: string): boolean {
    var decodedToken = this.jwtHelper.decodeToken(idToken);
    if (
      this.jwtHelper.isTokenExpired(idToken) ||
      decodedToken.nbf > moment().unix()
    ) {
      this.logout();
      return false;
    }
    this.setItem('id_token', idToken);
    return true;
  }

  public async logout() {
    this.callService.endCallFlow();
    const patient = this.patientService.patient;
    const identityPlatformSession = this.usesIdentityPlatform();
    if (identityPlatformSession) {
      await this.identityPlatformAuthenticationService.logout();
    }
    this.localStorageService.clear();
    this.registrationService.whiteLabel = false;
    this.registrationService.setProvider(null);
    this.webServiceStateHandler.resetPatientValidation();
    this.handlingUnauthorized = false;

    if(isNativeApp() && !identityPlatformSession) {
      this.logoutBrowserCapacitor();
    }
    // Workaround in dev to logout from PS auth0
    if(environment.ENVIRONMENT !== 'production' && patient?.psId) {
      this.getAuth0PS().logout({ returnTo: environment.PWA_URL });
    }
  }

  public async logoutBrowserCapacitor(federated: boolean = false) {
    const redirectUriCapacitor = this.capacitorService.getRedirectUri();
    const url = this.auth0.client.buildLogoutUrl({ returnTo: redirectUriCapacitor, federated });
    await Browser.open({ url: url})
    this.capacitorService.closeBrowser();
  }

  async handleAuth0Login(token: string, refreshToken?: string) {
    this.callService.endCallFlow();
    this.localStorageService.clearWithExceptions();
    localStorage.setItem('id_token', token);
    if(refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
      this.setRefreshTokenTimeout();
    }
    let patient = await this.patientService.auth();
    let provider = patient.provider
    this.saveData(patient, provider);
    this.setAuthenticationProvider(provider);
    // Re-evaluate locale using provider data to ensure compatibility with supported languages
    this.localeService.setLocaleInStorage([...window.navigator.languages], 'session', provider);
  }

  async restoreIdentityPlatformPatientSession(): Promise<void> {
    if (!this.usesIdentityPlatform() || !this.identityPlatformAuthenticationService.hasSession()) return;

    const patient = await this.patientService.auth();
    const provider = patient.provider;
    this.saveData(patient, provider);
    this.setAuthenticationProvider(provider);
    this.localeService.setLocaleInStorage([...window.navigator.languages], 'session', provider);
  }

  async signInWithIdentityPlatformEmail(email: string, password: string): Promise<void> {
    await this.authenticateIdentityPlatformEmail(email, password);
    await this.restoreIdentityPlatformPatientSession();
  }

  async authenticateIdentityPlatformEmail(email: string, password: string): Promise<void> {
    this.assertIdentityPlatformProviderSelected();
    await this.identityPlatformAuthenticationService.signInWithEmailAndPassword(email.trim(), password);
  }

  async createIdentityPlatformEmailAccount(email: string, password: string): Promise<void> {
    this.assertIdentityPlatformProviderSelected();
    await this.identityPlatformAuthenticationService.createUserWithEmailAndPassword(email.trim(), password);
    this.registrationService.updatePatient({ email: email.trim() });
  }

  async signInWithIdentityPlatformSocial(method: IdentityPlatformSocialMethod): Promise<void> {
    this.assertIdentityPlatformProviderSelected();
    await this.identityPlatformAuthenticationService.signInWithSocial(method);
  }

  hasPendingIdentityPlatformSocialCredential(): boolean {
    return this.identityPlatformAuthenticationService.hasPendingSocialCredential();
  }

  async linkPendingIdentityPlatformSocialCredential(): Promise<void> {
    await this.identityPlatformAuthenticationService.linkPendingSocialCredential();
  }

  async resetIdentityPlatformAuthenticationAttempt(): Promise<void> {
    await this.identityPlatformAuthenticationService.logout();
  }

  isIdentityPlatformPatientNotLinkedError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    const httpError = error as { status?: number; error?: { type?: string } };
    return httpError.status === 404 || httpError.error?.type === 'notFound';
  }

  async handleTokenLogin(token: string, appointmentId?: string) {
    this.callService.endCallFlow();
    var decodedToken = this.jwtHelper.decodeToken(token);
    if (decodedToken.nbf)
      localStorage.setItem('not_before', JSON.stringify(decodedToken.nbf));
    if (appointmentId) localStorage.setItem('appointment', appointmentId);
    localStorage.setItem('id_token', token);

    let patient = await this.patientService.getMe();
    let provider = patient.provider
    this.saveData(patient, provider);
    this.setAuthenticationProvider(provider);
  }

  private saveData(patient: any, provider: any) {
    this.registrationService.setPatient(patient);
    this.registrationService.setProvider(provider);
  }

  handleAuth0Registration(token: string, refreshToken?: string) {
    this.callService.endCallFlow();
    localStorage.setItem('id_token', token);
    if(refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
      this.setRefreshTokenTimeout();
    }
  }

  getBodyFromAuth0(token?: string): Auth0TokenBody {
    token = token ?? this.getToken();
    return this.jwtHelper.decodeToken(token!)!;
  }

  async generateRefreshToken(authorizationCode: string, redirectUri?: string): Promise<any> {
    redirectUri = isNativeApp() ? this.capacitorService.getRedirectUri() : redirectUri;

    this.requestService.post('/patient/refresh', { authorizationCode, redirectUri })
    .then((authResult: any) => {
      if(authResult?.refresh_token) {
        localStorage.setItem('refresh_token', authResult.refresh_token);
        this.setRefreshTokenTimeout();
      }
    })
  }

  public setRefreshTokenTimeout() {
    const self = this;
    const token = this.getToken();
    const tokenExpirationDate = this.jwtHelper.getTokenExpirationDate(token);
    const timeToExpire = moment(tokenExpirationDate).diff(moment(), 'milliseconds');

    setTimeout(() => {
      self.refreshToken((success: boolean) => {
        if(!success) {
          self.logout();
          self.redirectToLogin();
        }
      });
    }, timeToExpire - (REFRESH_TOKEN_SECONDS_BEFORE_EXPIRE * 1000));
  }

  public async refreshToken(cb?: any): Promise<any> {
    if(AuthService.refreshingToken) return true;

    const refresh_token = this.getRefreshToken();

    if(this.isAuthenticated() || (!this.isAuthenticated() && refresh_token))
      this.addRefreshTokenCallback(cb!);

    if (!refresh_token) return true;

    AuthService.refreshingToken = true;

    return this.requestService.post('/patient/token/refresh', { refreshToken: refresh_token }).then((authResult: any) => {
      this.setItem("id_token", authResult["id_token"]);
      this.setRefreshTokenTimeout();
      this.fireCallbacks(true);
      AuthService.refreshingToken = false;
      AuthService.refreshAttempts = REFRESH_TOKEN_ATTEMPTS;
    }).catch(() => {
      AuthService.refreshingToken = false;
      if(AuthService.refreshAttempts > 0) {
        AuthService.refreshAttempts --;
        return this.refreshToken();
      }
      AuthService.refreshAttempts = REFRESH_TOKEN_ATTEMPTS;
      this.fireCallbacks(false);
      return undefined;
    })
  }

  public addRefreshTokenCallback(cb: any): void {
    if(cb) AuthService.refreshTokenCbs.push(cb);
  }

  private fireCallbacks(success: boolean): void {
    AuthService.refreshTokenCbs.forEach((cb: any) => cb(success));
    AuthService.refreshTokenCbs = [];
  }

  public isRefreshingToken(): boolean {
    return AuthService.refreshingToken;
  }

  public handleUnauthorized(): boolean {
    if (this.appointmentService.inAppointment()) {
      this.customRouter.navigate([AppPaths.APPOINTMENT_END]);
      return true;
    }

    if (this.handlingUnauthorized) return false;
    this.handlingUnauthorized = true;

    const action = async () => {
      await this.logout();
      this.customRouter.navigate([`/${AppPaths.AUTH}`]);
    };

    this.matDialog.closeAll();
    this.spinnerService.hideSpinner();
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      disableClose: true,
      data: {
        title: 'expired_session_title',
        message: 'expired_session_message',
        canCancel: false,
        actions: [
          {
            message: 'permissions_accept',
            action
          }
        ]
      }
    });
    return false;
  }

  private redirectToLogin(): void {
    this.customRouter.navigate([`/${AppPaths.AUTH}`]);
  }

  private isSameSession(previousToken: string | null, nextToken: string | null): boolean {
    if (!previousToken || !nextToken) return false;

    try {
      const previousSubject = this.jwtHelper.decodeToken(previousToken)?.sub;
      const nextSubject = this.jwtHelper.decodeToken(nextToken)?.sub;
      return !!previousSubject && previousSubject === nextSubject;
    } catch {
      return false;
    }
  }

  public getSocialProviderAssociatedWithEmail(params: {email?: string, provider?: string, userId?:string}): Promise<{name:string}> {
    return this.requestService.get('/patient/socialProvider', params);
  }

  failedLoginWithEmailAndPassword(email: string) {
    this.requestService.post('/loginAttempt/failed', { email });
  }

  private hasValidLegacyToken(): boolean {
    const token = this.getToken();
    return !!token && !this.jwtHelper.isTokenExpired(token);
  }

  private clearIdentityPlatformContext(): void {
    localStorage.removeItem(AUTHENTICATION_BACKEND_KEY);
    localStorage.removeItem(IDENTITY_PLATFORM_PROVIDER_ID_KEY);
    this.registrationService.cleanData();
    this.registrationService.clearProvider();
  }

  private assertIdentityPlatformProviderSelected(): void {
    if (!this.usesIdentityPlatform()) {
      throw new Error('An Identity Platform Provider must be selected before authentication.');
    }
  }
}
